import { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import RepositoryResponse from '@/types/repositoryResponse';
import { StaffProfile } from '@/types/staffProfile';
import { StaffUserSession } from '@/types/staffSession';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';

export const staffProfileRepository = {
  async getMyProfile({
    host,
    currentUser
  }: {
    host: string;
    currentUser: StaffUserSession | null;
  }): Promise<RepositoryResponse<StaffProfile>> {
    if (!currentUser) return { isError: false };
    try {
      const { data } = await ajax.get({
        url: `${host}/api/staff_profiles/me/`
      });
      return { data: data as StaffProfile, isError: false };
    } catch (error: unknown) {
      if ((error as { response?: { status?: number } })?.response?.status === 404) {
        return { isError: false };
      }
      return defaultRepositoryError(error as AxiosError, 'Failed to get staff profile.');
    }
  },

  async getByStaffId({
    host,
    staffId
  }: {
    host: string;
    staffId: string;
  }): Promise<RepositoryResponse<StaffProfile>> {
    try {
      const { data } = await ajax.get({
        url: `${host}/api/staff_profiles/${staffId}/`
      });
      return { data: data as StaffProfile, isError: false };
    } catch (error: unknown) {
      if ((error as { response?: { status?: number } })?.response?.status === 404) {
        return { isError: false };
      }
      return defaultRepositoryError(error as AxiosError, 'Failed to get staff profile.');
    }
  },

  async getStaffProfiles({
    host,
    currentUser
  }: {
    host: string;
    currentUser: StaffUserSession | null;
  }): Promise<RepositoryResponse<StaffProfile[]>> {
    if (!currentUser) return { data: [], isError: false };
    try {
      const { data } = await ajax.get({
        url: `${host}/api/staff_profiles/`,
        params: { size: 200 }
      });
      // The list endpoint is paginated for users with view perms, and returns a
      // plain array otherwise. Normalize to a flat array of profiles.
      const profiles = Array.isArray(data) ? data : (data?.results ?? []);
      return { data: profiles as StaffProfile[], isError: false };
    } catch (error: unknown) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get staff profiles.');
    }
  }
};

const StaffProfileRepository = () => {
  const { host, currentUser } = useContext(AppContext);

  return {
    getMyProfile: (): Promise<RepositoryResponse<StaffProfile>> =>
      staffProfileRepository.getMyProfile({ host, currentUser }),

    getByStaffId: (staffId: string): Promise<RepositoryResponse<StaffProfile>> =>
      staffProfileRepository.getByStaffId({ host, staffId }),

    getStaffProfiles: (): Promise<RepositoryResponse<StaffProfile[]>> =>
      staffProfileRepository.getStaffProfiles({ host, currentUser })
  };
};

export default StaffProfileRepository;
